import { useEffect, useMemo, useState } from "react";
import {
  Search,
  Download,
  Filter,
  DollarSign,
  TrendingUp,
  Briefcase,
  Clock,
  LayoutGrid,
  List,
  Star,
  MapPin,
  CheckCircle2,
  XCircle,
  ChevronLeft,
  ChevronRight,
  History,
  RadioTower,
  RefreshCw,
  Loader2,
  Megaphone,
  Brain,
  BarChart3,
  Layers,
  Link2,
  ClipboardCheck,
  Bolt,
  Plus,
  Edit2,
  Trash2,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "../components/layout/AdminLayout";
import MilestoneModal from "../components/admin/MilestoneModal";
import ExecutiveOverviewPanel from "../components/admin/ExecutiveOverviewPanel";
import CostConfigDrawer, {
  emptyConfig,
} from "../components/admin/CostConfigDrawer";
import { axiosInstance } from "../lib/axios";
import {
  useAdminStore,
  type AdminProfile,
  type AdminProfileProject,
  type CostConfig,
  type ResourceValueDoc,
} from "../store/useAdminStore";

const fmtMoney = (n: number) => {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}K`;
  return `$${Math.round(n).toLocaleString()}`;
};

const fmtNum = (n: number | null | undefined) => {
  if (n === null || n === undefined) return "—";
  return n.toLocaleString();
};

const fmtResourceDoc = (r: ResourceValueDoc | null | undefined) => {
  if (!r || !r.amount) return "—";
  if (r.type === "hourly") return `$${r.amount}/hr`;
  if (r.type === "percentage") return `${r.amount}%`;
  return `$${r.amount}`;
};

const formatRelative = (iso?: string | null) => {
  if (!iso) return "Never";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins} minute${mins > 1 ? "s" : ""} ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs > 1 ? "s" : ""} ago`;
  const days = Math.floor(hrs / 24);
  return `${days} day${days > 1 ? "s" : ""} ago`;
};

const PROJECT_COPY: Record<string, string> = {
  "Irfan Baig":
    "Web Designer & Developer | WordPress, Shopify, Webflow | Migration",
  Nadeem: "Full-stack Developer | React, Node.js, MERN Stack | API Design",
  Farah: "Brand Designer | Visual Identity, Logo Design, Print | Illustration",
  Rizwan: "Marketing Specialist | SEO, Paid Ads, Content Strategy | Growth",
};

const statusBadge = (status: string) => {
  if (status === "Completed")
    return {
      cls: "bg-emerald-100 text-emerald-700",
      icon: <CheckCircle2 size={11} />,
      text: "COMPLETED",
    };
  if (status === "In Progress")
    return {
      cls: "bg-amber-100 text-amber-700",
      icon: <Loader2 size={11} className="animate-spin" />,
      text: "IN PROGRESS",
    };
  if (status === "On Hold")
    return {
      cls: "bg-orange-100 text-orange-700",
      icon: <Clock size={11} />,
      text: "ON HOLD",
    };
  if (status === "Cancelled")
    return {
      cls: "bg-rose-100 text-rose-700",
      icon: <XCircle size={11} />,
      text: "CANCELLED",
    };
  return {
    cls: "bg-blue-100 text-blue-700",
    icon: <RadioTower size={11} />,
    text: "ACTIVE",
  };
};

const Ring = ({
  value,
  label,
  color,
}: {
  value: number;
  label: string;
  color: string;
}) => {
  const size = 112;
  const stroke = 10;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, value));
  const offset = c - (c * pct) / 100;
  return (
    <div className="flex flex-col items-center">
      <div className="relative" style={{ width: size, height: size }}>
        <svg
          width={size}
          height={size}
          className="-rotate-90"
        >
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="transparent"
            stroke="#f1f5f9"
            strokeWidth={stroke}
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="transparent"
            stroke={color}
            strokeWidth={stroke}
            strokeDasharray={c}
            strokeDashoffset={offset}
            strokeLinecap="round"
          />
        </svg>
        <span className="absolute inset-0 grid place-items-center text-[24px] font-black text-slate-900">
          {pct}%
        </span>
      </div>
      <span className="mt-3 text-[11px] font-extrabold text-slate-500 uppercase tracking-[0.15em]">
        {label}
      </span>
    </div>
  );
};

const StatCard = ({
  label,
  value,
  subtitle,
  icon: Icon,
  highlighted = false,
}: {
  label: string;
  value: string;
  subtitle: string;
  icon: any;
  highlighted?: boolean;
}) => (
  <div
    className={`bg-white rounded-2xl p-6 border-l-4 flex flex-col justify-between shadow-[0_4px_6px_-1px_rgba(0,0,0,0.05)] ${
      highlighted ? "border-blue-600" : "border-slate-800/90"
    }`}
  >
    <div className="flex items-center justify-between mb-4">
      <span className="text-[11px] font-bold text-slate-500 uppercase tracking-[0.15em]">
        {label}
      </span>
      <div
        className={`w-12 h-12 grid place-items-center rounded-xl ${
          highlighted ? "bg-blue-50 text-blue-600" : "bg-slate-100 text-slate-700"
        }`}
      >
        <Icon size={22} />
      </div>
    </div>
    <div>
      <h3
        className={`text-[42px] font-black leading-none tracking-tight ${
          highlighted ? "text-blue-600" : "text-slate-900"
        }`}
      >
        {value}
      </h3>
      <p className="text-[12px] text-slate-500 mt-3 font-medium">{subtitle}</p>
    </div>
  </div>
);

const AiMetric = ({
  icon: Icon,
  iconClass,
  label,
  value,
  valueClass = "text-slate-900",
  trailingIcon,
}: {
  icon: any;
  iconClass: string;
  label: string;
  value: string;
  valueClass?: string;
  trailingIcon?: React.ReactNode;
}) => (
  <div className="p-4 rounded-xl border border-slate-100 bg-slate-50/60 flex items-center justify-between">
    <div className="flex items-center gap-3 min-w-0">
      <div className={`w-10 h-10 rounded-lg grid place-items-center ${iconClass}`}>
        <Icon size={18} />
      </div>
      <span className="text-[12px] font-bold text-slate-500 uppercase tracking-wider truncate">
        {label}
      </span>
    </div>
    <div className="flex items-center gap-1.5 shrink-0">
      <span className={`text-[18px] font-black ${valueClass}`}>{value}</span>
      {trailingIcon}
    </div>
  </div>
);

const RailLink = ({
  icon: Icon,
  label,
  active,
  onClick,
}: {
  icon: any;
  label: string;
  active?: boolean;
  onClick?: () => void;
}) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center px-4 py-3 gap-3 text-[13px] font-medium transition ${
      active
        ? "bg-blue-50 text-blue-700 border-r-4 border-blue-600"
        : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
    }`}
  >
    <Icon size={18} />
    <span>{label}</span>
  </button>
);

const AdminDashboardPage = () => {
  const navigate = useNavigate();
  const {
    overview,
    profiles,
    profileProjects,
    recentUploads,
    accountPerformance,
    costConfigs,
    loading,
    fetchAll,
    fetchProfileProjects,
    fetchAccountPerformance,
    fetchCostConfigs,
  } = useAdminStore();

  useEffect(() => {
    fetchAccountPerformance({ period: "all" });
    fetchCostConfigs({ limit: 100 });
  }, [fetchAccountPerformance, fetchCostConfigs]);

  const [activeProfileId, setActiveProfileId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [uploadsView, setUploadsView] = useState<"grid" | "list">("grid");
  const [milestoneProject, setMilestoneProject] =
    useState<AdminProfileProject | null>(null);
  const [railSection, setRailSection] = useState<
    "performance" | "financials" | "resources"
  >("resources");
  const [view, setView] = useState<"overview" | "account">("overview");

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    const first = profiles[0];
    if (!activeProfileId && first) {
      setActiveProfileId(first.accountId);
    }
  }, [profiles, activeProfileId]);

  useEffect(() => {
    if (activeProfileId && !profileProjects[activeProfileId]) {
      fetchProfileProjects(activeProfileId);
    }
  }, [activeProfileId, profileProjects, fetchProfileProjects]);

  const activeProfile: AdminProfile | undefined = useMemo(
    () => profiles.find((p) => p.accountId === activeProfileId),
    [profiles, activeProfileId]
  );

  const projects = activeProfileId
    ? profileProjects[activeProfileId] || []
    : [];
  const filteredProjects = projects.filter((p) => {
    const matchesSearch =
      !search || p.name.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = !statusFilter || p.projectStatus === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const initials = (name: string) =>
    name.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase();

  return (
    <AdminLayout>
      <div>
        <div className="p-8 space-y-6">

            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex bg-[#eff4ff] p-1 rounded-xl overflow-x-auto max-w-full">
                <button
                  onClick={() => setView("overview")}
                  className={`px-6 py-2 rounded-lg text-[14px] font-semibold tracking-[0.02em] transition-all whitespace-nowrap ${
                    view === "overview"
                      ? "bg-white text-[#0070f3] shadow-sm"
                      : "text-slate-600 hover:text-[#0058c3]"
                  }`}
                >
                  Overview
                </button>
                {profiles.map((p) => {
                  const active = view === "account" && p.accountId === activeProfileId;
                  return (
                    <button
                      key={p.accountId}
                      onClick={() => { setView("account"); setActiveProfileId(p.accountId); }}
                      className={`px-6 py-2 rounded-lg text-[14px] font-semibold tracking-[0.02em] transition-all whitespace-nowrap ${
                        active
                          ? "bg-white text-[#0070f3] shadow-sm"
                          : "text-slate-600 hover:text-[#0058c3]"
                      }`}
                    >
                      {p.name}
                    </button>
                  );
                })}
              </div>
              <div className="flex items-center gap-2 text-[12px] text-slate-500">
                <History size={14} />
                <span>Last synced: 2 minutes ago</span>
                <button
                  onClick={() => fetchAll()}
                  className="ml-2 inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 font-semibold"
                  title="Refresh"
                >
                  <RefreshCw
                    size={13}
                    className={loading ? "animate-spin" : ""}
                  />
                </button>
              </div>
            </div>

            {view === "overview" ? (
              <ExecutiveOverviewPanel />
            ) : (
              <>

            {activeProfile && (
              <div className="bg-white rounded-xl p-8 border border-slate-200 shadow-[0_4px_6px_-1px_rgba(0,0,0,0.05)]">
                <div className="grid grid-cols-12 gap-8 items-center">

                  <div className="col-span-12 md:col-span-2 flex flex-col gap-3 items-center">
                    <div className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-100 to-blue-200 grid place-items-center text-blue-700 text-3xl font-black border-4 border-white shadow-md">
                      {initials(activeProfile.name)}
                    </div>
                    <h4 className="text-slate-900 text-center text-[20px] font-bold tracking-tight">
                      {activeProfile.name}
                    </h4>
                  </div>

                  <div className="col-span-12 md:col-span-6 flex flex-col items-start md:border-l border-slate-200 md:pl-8">
                    <div className="flex items-center gap-1 text-[#14a800] mb-3">
                      <svg
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        className="w-9 h-9"
                      >
                        <path d="M18.561 13.158c-1.102 0-2.135-.467-3.074-1.227l.228-1.076.008-.042c.207-1.143.849-3.06 2.838-3.06 1.062 0 1.924.861 1.924 1.924v3.557c0 .51.413.924.924.924s.924-.413.924-.924V9.677c0-2.08-1.692-3.772-3.772-3.772-2.31 0-3.736 1.632-4.547 3.538-.606-1.017-1.488-2.322-2.316-3.136l-.05-.05c-.347-.34-.735-.512-1.155-.512-.832 0-1.512.68-1.512 1.512 0 .41.166.797.467 1.09l.013.013c.78.751 2.215 2.131 2.83 3.992l-.934 4.41c-.41 1.942-1.89 3.328-3.784 3.328-2.08 0-3.772-1.692-3.772-3.772V9.677c0-.51-.413-.924-.924-.924s-.924.413-.924.924v6.643c0 3.102 2.523 5.626 5.62 5.626 3.102 0 5.626-2.523 5.626-5.62l.836-3.953c.69.544 1.396.953 2.124.953 1.062 0 1.924-.861 1.924-1.924v-1.64c0-.51-.413-.924-.924-.924s-.924.413-.924.924v1.64c0 .05-.04.091-.091.091z" />
                      </svg>
                      <span className="text-[24px] font-extrabold tracking-tighter">
                        upwork
                      </span>
                    </div>
                    <span className="px-4 py-1 bg-[#0058c3] text-white rounded-full text-[11px] font-bold uppercase tracking-wide mb-3 shadow-sm">
                      {activeProfile.rankingBadge || "Top Rated Plus"}
                    </span>
                    <p className="text-[14px] font-medium text-slate-600 mb-4 leading-relaxed">
                      {PROJECT_COPY[activeProfile.name] ||
                        "Business Developer | Multiple specialties"}
                    </p>
                    <div className="flex items-center gap-6">
                      <div className="flex items-center text-[#006a61]">
                        <Star size={16} className="fill-current" />
                        <span className="font-bold text-[12px] ml-1">4.8</span>
                        <span className="text-slate-500 text-[12px] ml-1 font-normal">
                          (142 reviews)
                        </span>
                      </div>
                      <p className="text-[11px] text-slate-500 flex items-center gap-1 font-semibold">
                        <MapPin size={14} />
                        Karachi, Pakistan
                      </p>
                    </div>
                  </div>

                  <div className="col-span-12 md:col-span-4 flex justify-end items-center gap-10">
                    <Ring
                      value={activeProfile.conversionRate}
                      label="Conversion Rate"
                      color="#0058c3"
                    />
                    <Ring
                      value={activeProfile.connectsUsedPct}
                      label="Connects Used"
                      color="#006a61"
                    />
                  </div>
                </div>
              </div>
            )}

            {activeProfile && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard
                  label="Job Success Rate"
                  value={
                    activeProfile.jobSuccessRate !== null
                      ? `${activeProfile.jobSuccessRate}%`
                      : "—"
                  }
                  subtitle="Current account standing"
                  icon={TrendingUp}
                  highlighted
                />
                <StatCard
                  label="Total Earnings"
                  value={
                    fmtMoney(activeProfile.totalEarnings) +
                    (activeProfile.totalEarnings > 0 ? "+" : "")
                  }
                  subtitle="Life-time earnings"
                  icon={DollarSign}
                />
                <StatCard
                  label="Total Jobs"
                  value={fmtNum(activeProfile.totalJobs)}
                  subtitle="Completed projects"
                  icon={Briefcase}
                />
                <StatCard
                  label="Total Hours"
                  value={fmtNum(activeProfile.totalHours)}
                  subtitle="Logged work hours"
                  icon={Clock}
                />
              </div>
            )}

            <div className="bg-white rounded-xl border border-slate-200 shadow-[0_4px_6px_-1px_rgba(0,0,0,0.05)] overflow-hidden">
              <div className="p-6 border-b border-slate-100 space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="text-[24px] font-bold text-slate-900 tracking-tight">
                    Projects Overview
                  </h3>
                  <div className="flex gap-2">
                    <button className="inline-flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-lg text-[13px] text-slate-600 hover:bg-slate-50 transition">
                      <Download size={16} />
                      Export CSV
                    </button>
                    <button className="bg-[#0058c3] hover:opacity-90 text-white px-4 py-2 rounded-lg text-[13px] font-semibold transition">
                      Create Project
                    </button>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="relative flex-1 min-w-[260px]">
                    <Search
                      size={16}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                    />
                    <input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search projects by name..."
                      className="w-full pl-10 pr-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-[13px] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <select className="bg-slate-50 border border-slate-200 rounded-lg text-[13px] px-3 py-2.5 text-slate-600">
                    <option>Type: All</option>
                    <option>Fixed Price</option>
                    <option>Hourly</option>
                  </select>
                  <select className="bg-slate-50 border border-slate-200 rounded-lg text-[13px] px-3 py-2.5 text-slate-600">
                    <option>Owned By: All</option>
                  </select>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="bg-slate-50 border border-slate-200 rounded-lg text-[13px] px-3 py-2.5 text-slate-600"
                  >
                    <option value="">Status: All</option>
                    <option>Not Started</option>
                    <option>In Progress</option>
                    <option>On Hold</option>
                    <option>Completed</option>
                    <option>Cancelled</option>
                  </select>
                  <select className="bg-slate-50 border border-slate-200 rounded-lg text-[13px] px-3 py-2.5 text-slate-600">
                    <option>Origin: All</option>
                    <option>Organic</option>
                    <option>Bid</option>
                  </select>
                  <button className="p-2.5 text-slate-500 hover:bg-slate-100 rounded-lg border border-slate-200">
                    <Filter size={16} />
                  </button>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-50/80 border-b border-slate-200">
                      <Th>Project Title</Th>
                      <Th>Owned By (BD)</Th>
                      <Th>Assigned To</Th>
                      <Th style={{ width: 150 }}>Completion</Th>
                      <Th align="center">Milestones</Th>
                      <Th align="right">Total</Th>
                      <Th align="right">Escrow</Th>
                      <Th align="right">Released</Th>
                      <Th align="center">Origin</Th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredProjects.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="px-6 py-14 text-center text-slate-400 text-[13px]">
                          {loading
                            ? "Loading projects..."
                            : "No projects for this profile yet."}
                        </td>
                      </tr>
                    ) : (
                      filteredProjects.map((p) => {
                        const sb = statusBadge(p.projectStatus);
                        return (
                          <tr
                            key={p._id}
                            className="hover:bg-slate-50/50 transition"
                          >
                            <td className="px-6 py-5 align-top">
                              <span className="text-[14px] font-bold text-[#0070f3] tracking-[0.02em]">
                                {p.name}
                              </span>
                              <div className="flex mt-1.5 items-center gap-1.5">
                                <span
                                  className={`text-[10px] px-2 py-0.5 rounded-full font-bold flex items-center gap-1 ${sb.cls}`}
                                >
                                  {sb.icon}
                                  {sb.text}
                                </span>
                                <span className="text-[10px] text-slate-500 font-bold uppercase tracking-tight">
                                  • {p.priceModel === "fixed" ? "FIXED PRICE" : "HOURLY"}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-5 text-[13px] text-slate-900 font-medium">
                              {p.ownedBy}
                            </td>
                            <td className="px-6 py-5">
                              <div className="flex items-center gap-2">
                                <div className="w-6 h-6 rounded-full bg-[#d8e2ff] grid place-items-center text-[10px] font-bold text-[#0058c3]">
                                  {initials(p.assignedTo)}
                                </div>
                                <span className="text-[13px] text-slate-900">
                                  {p.assignedTo}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-5">
                              <div className="flex flex-col gap-1.5">
                                <span
                                  className={`text-[11px] font-bold ${
                                    p.completionPct === 100
                                      ? "text-[#006a61]"
                                      : "text-slate-500"
                                  }`}
                                >
                                  {p.completionPct}%
                                </span>
                                <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
                                  <div
                                    className={`h-full rounded-full ${
                                      p.completionPct === 100
                                        ? "bg-[#006a61]"
                                        : "bg-[#0058c3]"
                                    }`}
                                    style={{ width: `${p.completionPct}%` }}
                                  />
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-5 text-center">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setMilestoneProject(p);
                                }}
                                className={`px-2 py-1 rounded-lg text-[11px] font-semibold cursor-pointer hover:ring-2 hover:ring-blue-300 transition ${
                                  p.completionPct === 100
                                    ? "bg-[#86f2e4]/40 text-[#006a61]"
                                    : "bg-[#e5eeff] text-slate-700"
                                }`}
                                title="View milestone breakdown"
                              >
                                {p.milestonesReleased}/{p.milestonesTotal} Completed
                              </button>
                            </td>
                            <td className="px-6 py-5 text-right text-[13px] font-mono font-bold text-slate-900">
                              {fmtMoney(p.total)}
                            </td>
                            <td className="px-6 py-5 text-right text-[13px] font-mono text-slate-500">
                              {fmtMoney(p.escrow)}
                            </td>
                            <td className="px-6 py-5 text-right text-[13px] font-mono text-[#006a61]">
                              {fmtMoney(p.released)}
                            </td>
                            <td className="px-6 py-5">
                              <div className="flex justify-center">
                                {p.origin === "BID" ? (
                                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#d8e2ff]/60 text-[#004397] text-[11px] font-bold">
                                    <Megaphone size={13} /> BID
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#86f2e4]/40 text-[#006f66] text-[11px] font-bold">
                                    <Brain size={13} /> ORGANIC
                                  </span>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {filteredProjects.length > 0 && (
                <div className="bg-slate-50/50 border-t border-slate-200 px-6 py-4 flex items-center justify-between text-[12px] text-slate-600">
                  <span>
                    Showing <strong>1-{filteredProjects.length}</strong> of{" "}
                    <strong>{filteredProjects.length}</strong> projects
                  </span>
                  <div className="flex gap-1">
                    <button className="p-1.5 rounded text-slate-400 cursor-not-allowed">
                      <ChevronLeft size={18} />
                    </button>
                    <button className="px-3 py-1.5 rounded bg-white border border-slate-200 font-bold text-[#0058c3] shadow-sm">
                      1
                    </button>
                    <button className="px-3 py-1.5 rounded hover:bg-white border border-transparent hover:border-slate-200 transition">
                      2
                    </button>
                    <button className="px-3 py-1.5 rounded hover:bg-white border border-transparent hover:border-slate-200 transition">
                      3
                    </button>
                    <button className="p-1.5 rounded hover:bg-white border border-transparent hover:border-slate-200 text-slate-600 transition">
                      <ChevronRight size={18} />
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-12 gap-6 items-stretch">

              <ProfileCostSection
                activeProfileId={activeProfileId}
                activeProfileName={activeProfile?.name || null}
                configs={costConfigs?.items || []}
                onManage={() => navigate("/admin/cost-management")}
              />

              <ProfilePerformanceSection
                activeProfileId={activeProfileId}
                accountPerformance={accountPerformance}
              />
            </div>

            <div className="bg-white rounded-xl border border-slate-200 shadow-[0_4px_6px_-1px_rgba(0,0,0,0.05)] p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-[18px] font-bold text-slate-900">
                  Recent Uploads & Screenshot Validation
                </h3>
                <div className="flex gap-2">
                  <button
                    onClick={() => setUploadsView("grid")}
                    className={`p-2 border rounded-lg transition ${
                      uploadsView === "grid"
                        ? "bg-blue-50 border-blue-200 text-[#0058c3]"
                        : "border-slate-200 text-slate-500 hover:bg-slate-50"
                    }`}
                  >
                    <LayoutGrid size={18} />
                  </button>
                  <button
                    onClick={() => setUploadsView("list")}
                    className={`p-2 border rounded-lg transition ${
                      uploadsView === "list"
                        ? "bg-blue-50 border-blue-200 text-[#0058c3]"
                        : "border-slate-200 text-slate-500 hover:bg-slate-50"
                    }`}
                  >
                    <List size={18} />
                  </button>
                </div>
              </div>

              {recentUploads.length === 0 ? (
                <div className="text-center text-slate-400 py-12 text-[13px]">
                  No screenshots uploaded yet — BD ka jab pehla snapshot aaye ga,
                  yahan dikhega.
                </div>
              ) : uploadsView === "grid" ? (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  {recentUploads.map((u, i) => (
                    <div
                      key={i}
                      className="group relative aspect-video rounded-lg overflow-hidden border border-slate-200"
                    >
                      <img
                        src={u.url}
                        alt=""
                        className="w-full h-full object-cover transition-transform group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition flex items-center justify-center gap-2">
                        <button className="bg-white p-2 rounded-full text-[#006a61] hover:bg-emerald-50 transition">
                          <CheckCircle2 size={18} />
                        </button>
                        <button className="bg-white p-2 rounded-full text-[#ba1a1a] hover:bg-rose-50 transition">
                          <XCircle size={18} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {recentUploads.map((u, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-3 py-3 hover:bg-slate-50/50 rounded-md px-2"
                    >
                      <div className="w-14 h-14 rounded-md overflow-hidden bg-slate-100">
                        <img
                          src={u.url}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] font-semibold text-slate-900 truncate">
                          {u.platformAccount}
                        </p>
                        <p className="text-[11px] text-slate-500">
                          Uploaded by {u.uploadedBy} ·{" "}
                          {formatRelative(u.uploadedAt)}
                        </p>
                      </div>
                      <div className="flex gap-1">
                        <button className="px-3 py-1.5 text-[12px] font-semibold text-[#006a61] hover:bg-emerald-50 rounded-md inline-flex items-center gap-1">
                          <CheckCircle2 size={14} />
                          Approve
                        </button>
                        <button className="px-3 py-1.5 text-[12px] font-semibold text-[#ba1a1a] hover:bg-rose-50 rounded-md inline-flex items-center gap-1">
                          <XCircle size={14} />
                          Reject
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
              </>
            )}
          </div>
        </div>

      {milestoneProject && (
        <MilestoneModal
          projectName={milestoneProject.name}
          projectType={milestoneProject.projectType}
          milestones={milestoneProject.milestoneItems}
          onClose={() => setMilestoneProject(null)}
        />
      )}
    </AdminLayout>
  );
};

const Th = ({
  children,
  align = "left",
  style,
}: {
  children: React.ReactNode;
  align?: "left" | "center" | "right";
  style?: React.CSSProperties;
}) => (
  <th
    className={`px-4 py-4 text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap text-${align}`}
    style={style}
  >
    {children}
  </th>
);

const ProfileCostSection = ({
  activeProfileId,
  activeProfileName,
  configs,
  onManage,
}: {
  activeProfileId: string | null;
  activeProfileName: string | null;
  configs: CostConfig[];
  onManage: () => void;
}) => {
  const {
    createCostConfig,
    updateCostConfig,
    deleteCostConfig,
    fetchCostConfigs,
  } = useAdminStore();

  const [accounts, setAccounts] = useState<{ _id: string; name: string }[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<Partial<CostConfig> | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axiosInstance
      .get("/upwork-accounts")
      .then((res) => setAccounts(res.data))
      .catch(() => setAccounts([]));
  }, []);

  const relevant = configs.filter((c) => {
    if (!c.upworkAccount) return true;
    if (!activeProfileId) return false;
    return c.upworkAccount._id === activeProfileId;
  });

  const openAdd = () => {

    setEditing(
      emptyConfig({
        upworkAccount: activeProfileId
          ? ({ _id: activeProfileId, name: activeProfileName || "" } as any)
          : null,
      })
    );
    setDrawerOpen(true);
  };

  const openEdit = (cfg: CostConfig) => {
    setEditing({ ...cfg });
    setDrawerOpen(true);
  };

  const handleSave = async () => {
    if (!editing) return;
    setSaving(true);
    const payload = {
      ...editing,
      upworkAccount:
        typeof editing.upworkAccount === "object" && editing.upworkAccount
          ? editing.upworkAccount._id
          : editing.upworkAccount || null,
    };
    const result = editing._id
      ? await updateCostConfig(editing._id, payload as Partial<CostConfig>)
      : await createCostConfig(payload as Partial<CostConfig>);
    setSaving(false);
    if (result) {
      setDrawerOpen(false);
      setEditing(null);

      fetchCostConfigs({ limit: 100 });
    }
  };

  const handleDelete = async (cfg: CostConfig) => {
    if (!confirm(`Delete cost config for ${cfg.projectType} (${cfg.clientTier})?`))
      return;
    const ok = await deleteCostConfig(cfg._id);
    if (ok) fetchCostConfigs({ limit: 100 });
  };

  return (
    <div className="col-span-12 lg:col-span-8 bg-white rounded-xl border border-slate-200 shadow-[0_4px_6px_-1px_rgba(0,0,0,0.05)] p-6 flex flex-col">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="text-[18px] font-bold text-slate-900">
            Resource Cost Management
          </h3>
          <p className="text-[11px] text-slate-500 mt-0.5">
            Edit configs for{" "}
            <strong className="text-slate-700">
              {activeProfileName || "current profile"}
            </strong>{" "}
            inline · or open full Cost Management page
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={openAdd}
            className="bg-[#0058c3] hover:opacity-90 text-white text-[12px] font-semibold px-4 py-2 rounded-lg flex items-center gap-2 transition"
          >
            <Plus size={14} />
            Add Config
          </button>
          <button
            onClick={onManage}
            className="border border-slate-200 hover:bg-slate-50 text-slate-700 text-[12px] font-semibold px-4 py-2 rounded-lg flex items-center gap-2 transition"
          >
            <Layers size={14} />
            Manage All
          </button>
        </div>
      </div>
      <div className="overflow-x-auto flex-1">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50/50">
              <Th>Project Type</Th>
              <Th>Tier</Th>
              <Th>Scope</Th>
              <Th>Fixed</Th>
              <Th>Range</Th>
              <Th>Designer</Th>
              <Th>Coordinator</Th>
              <Th>Commission</Th>
              <Th>Upwork Fee</Th>
              <Th align="center">Actions</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {relevant.length === 0 ? (
              <tr>
                <td
                  colSpan={10}
                  className="px-4 py-10 text-center text-slate-400 text-[13px]"
                >
                  No cost configs for this profile.{" "}
                  <button
                    onClick={openAdd}
                    className="text-blue-600 hover:text-blue-800 font-semibold underline"
                  >
                    Add one now
                  </button>
                </td>
              </tr>
            ) : (
              relevant.map((c) => (
                <tr
                  key={c._id}
                  onClick={() => openEdit(c)}
                  className="hover:bg-slate-50/40 transition cursor-pointer"
                >
                  <td className="px-4 py-4">
                    <span className="text-[13px] font-bold text-[#0070f3]">
                      {c.projectType}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <span
                      className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ring-1 ${
                        c.clientTier === "Premium"
                          ? "bg-amber-100 text-amber-800 ring-amber-200"
                          : c.clientTier === "Standard"
                          ? "bg-blue-100 text-blue-700 ring-blue-200"
                          : "bg-slate-100 text-slate-700 ring-slate-200"
                      }`}
                    >
                      {c.clientTier}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-[11px] text-slate-500">
                    {c.upworkAccount ? c.upworkAccount.name : "Global"}
                  </td>
                  <td className="px-4 py-4 text-[13px] text-slate-900 font-mono">
                    {fmtResourceDoc(c.fixedCost)}
                  </td>
                  <td className="px-4 py-4 text-[12px] text-slate-500 font-mono">
                    {c.rangeCost?.min || c.rangeCost?.max
                      ? `$${c.rangeCost.min}-$${c.rangeCost.max}`
                      : "—"}
                  </td>
                  <td className="px-4 py-4 text-[13px] text-slate-900 font-mono">
                    {fmtResourceDoc(c.graphicDesignerCost)}
                  </td>
                  <td className="px-4 py-4 text-[13px] text-slate-900 font-mono">
                    {fmtResourceDoc(c.coordinatorCost)}
                  </td>
                  <td className="px-4 py-4 text-[13px] text-slate-900">
                    {c.commission}%
                  </td>
                  <td className="px-4 py-4 text-[13px] font-bold text-[#006a61]">
                    {c.upworkFee}%
                  </td>
                  <td
                    className="px-4 py-4"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex justify-center gap-1">
                      <button
                        onClick={() => openEdit(c)}
                        className="p-1 text-slate-500 hover:text-blue-600 transition"
                        title="Edit"
                      >
                        <Edit2 size={14} />
                      </button>
                      <button
                        onClick={() => handleDelete(c)}
                        className="p-1 text-slate-500 hover:text-rose-600 transition"
                        title="Delete"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {drawerOpen && editing && (
        <CostConfigDrawer
          value={editing}
          accounts={accounts}
          saving={saving}
          onChange={setEditing}
          onSave={handleSave}
          onClose={() => {
            setDrawerOpen(false);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
};

const ProfilePerformanceSection = ({
  activeProfileId,
  accountPerformance,
}: {
  activeProfileId: string | null;
  accountPerformance: ReturnType<
    typeof useAdminStore.getState
  >["accountPerformance"];
}) => {
  const card = accountPerformance?.accounts.find(
    (a) => a.accountId === activeProfileId
  );

  return (
    <div className="col-span-12 lg:col-span-4 bg-white rounded-xl border border-slate-200 shadow-[0_4px_6px_-1px_rgba(0,0,0,0.05)] p-6 flex flex-col">
      <div className="flex items-center gap-2 mb-1">
        <BarChart3 size={20} className="text-[#0058c3]" />
        <h3 className="text-[18px] font-bold text-slate-900">
          Performance Snapshot
        </h3>
      </div>
      <p className="text-[11px] text-slate-500 mb-5">
        System-calculated metrics for this profile
      </p>
      {!card ? (
        <div className="flex-1 grid place-items-center text-slate-400 text-[12px]">
          Select a profile above to view metrics
        </div>
      ) : (
        <div className="space-y-3 flex-1">
          <PerfRow
            icon={Link2}
            iconClass="bg-blue-100 text-[#0058c3]"
            label="Avg. Connects per Bid"
            value={String(card.metrics.avgConnectsPerBid || "—")}
          />
          <PerfRow
            icon={DollarSign}
            iconClass="bg-emerald-100 text-emerald-700"
            label="Avg. Project Amount"
            value={
              card.metrics.avgProjectAmount
                ? fmtMoney(card.metrics.avgProjectAmount)
                : "—"
            }
          />
          <PerfRow
            icon={TrendingUp}
            iconClass="bg-blue-100 text-[#0058c3]"
            label="Top Converting Category"
            value={card.metrics.topConvertingCategory || "—"}
          />
          <PerfRow
            icon={ClipboardCheck}
            iconClass="bg-violet-100 text-violet-700"
            label="Avg. Proposals Created"
            value={`${card.metrics.avgProposalsPerMonth}/Mo`}
          />
          <div className="p-4 rounded-xl border border-amber-100 bg-gradient-to-br from-amber-50 to-amber-100/40">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg grid place-items-center bg-amber-200/60 text-amber-700">
                  <Bolt size={18} />
                </div>
                <div>
                  <p className="text-[10px] font-bold text-amber-700 uppercase tracking-wider">
                    Bid Efficiency Score
                  </p>
                  <p className="text-[10px] text-amber-700/70">
                    Win-rate · Connect-eff · Deal-size · Category
                  </p>
                </div>
              </div>
              <div className="text-right">
                <span className="text-[26px] font-black text-amber-700 leading-none">
                  {card.metrics.bidEfficiencyScore}
                </span>
                <p className="text-[9px] uppercase tracking-wider font-bold text-amber-700/70">
                  / 100
                </p>
              </div>
            </div>
            <div className="mt-3 h-1.5 w-full bg-amber-200/40 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-amber-400 to-amber-600 rounded-full"
                style={{ width: `${card.metrics.bidEfficiencyScore}%` }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const PerfRow = ({
  icon: Icon,
  iconClass,
  label,
  value,
}: {
  icon: any;
  iconClass: string;
  label: string;
  value: string;
}) => (
  <div className="p-4 rounded-xl border border-slate-100 bg-slate-50/60 flex items-center justify-between">
    <div className="flex items-center gap-3 min-w-0">
      <div className={`w-10 h-10 rounded-lg grid place-items-center ${iconClass}`}>
        <Icon size={18} />
      </div>
      <span className="text-[12px] font-bold text-slate-500 uppercase tracking-wider truncate">
        {label}
      </span>
    </div>
    <span className="text-[16px] font-black text-slate-900 shrink-0">
      {value}
    </span>
  </div>
);

export default AdminDashboardPage;
